import React from 'react';

import { Tableau } from '~commonInfra/utils/TableauLog';
import { CompositeSessionInfoSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { SettingsFormikContainer, SettingsFormikValues } from '~src/settings/components/common/SettingsFormikContainer';
import { useSelector } from '~src/settings/general-settings/utils';
import { ISiteAdminSettings, ISiteSettingsForSiteAdmin, ISiteSubscriptionSettings } from '~src/settings/types/ISiteSettingsForSiteAdmin';
import { filterOutServerAdminOnlySiteSettingsV2 } from '~src/settings/utils/siteSettingsV2Filter';
import { Sites } from '~src/siteManagement/services/resources/Sites';

interface LoadedSettings {
  siteId: string;
  siteAdminSettings: ISiteAdminSettings;
  // Not edited by this section, but the server requires it to be round-tripped on every
  // updateSiteSettingsForSiteAdmin call when server subscriptions are enabled (error 80005:
  // "subscriptionSettings is required when server subscriptions are enabled"). The General tab
  // forwards it unconditionally for the same reason (see settingsToSend.subscriptionSettings in
  // ReactSiteSettingsForSiteAdminSubFormik's useSubmitHandler).
  subscriptionSettings?: ISiteSubscriptionSettings;
}

// Data-loading host for the Packages tab's siteSettingsV2 sections. Fetches the settings bag,
// owns the submit/round-trip logic, and renders a SettingsFormikContainer into the SettingsCoordinator
// context provided by PackagesSiteSection. Add new Packages settings sections to the sections array below.
export function PackagesSettings(): React.ReactElement | null {
  const showSection = useShowSection();
  const [loaded, setLoaded] = React.useState<LoadedSettings | null>(null);

  React.useEffect(() => {
    if (!showSection) {
      return;
    }
    let isMounted = true;
    Sites.instance.getCurrentSiteSettingsForSiteAdmin()
      .then(result => {
        if (isMounted) {
          // getSiteSettingsForSiteAdmin returns the site id under `id` at runtime, not the
          // typed `siteId` field (see getSiteIdFromGeneralSettings in
          // ReactSiteSettingsForSiteAdminSubFormik). Reading result.siteId alone yields
          // undefined, which makes updateSiteSettingsForSiteAdmin omit the required `id`
          // param (server error 24: "Expected parameter 'id' not found").
          const siteId = (result as ISiteSettingsForSiteAdmin & { id?: string }).id ?? result.siteId;
          setLoaded({
            siteId,
            siteAdminSettings: result.siteAdminSettings as ISiteAdminSettings,
            subscriptionSettings: result.subscriptionSettings,
          });
        }
      })
      .catch((error: Error) => {
        Tableau.Logger().error(error);
      });
    return () => { isMounted = false; };
  }, [showSection]);

  if (!showSection || !loaded) {
    return null;
  }

  const handleSubmit = async (
    newSettings: ISiteAdminSettings
  ): Promise<SettingsFormikValues<ISiteAdminSettings> | void> => {
    const settingsToSend = filterOutServerAdminOnlySiteSettingsV2(newSettings);
    const result = await Sites.instance.updateSiteSettingsForSiteAdmin(
      loaded.siteId, settingsToSend, loaded.subscriptionSettings
    );

    if (result?.errors?.length) {
      throw result.errors.map((e: any) => Sites.instance.errorMessage(e.code, null));
    }

    return { settings: settingsToSend };
  };

  return (
    <SettingsFormikContainer
      formName={'PackagesSettings'}
      settings={loaded.siteAdminSettings}
      siteId={loaded.siteId}
      sections={['PackagesAdminSettingsSection', 'AllowedExtensionOriginsSection', 'ExtensionPackageRestAllowlistSection']}
      onSubmit={handleSubmit}
    />
  );
}

// packagesEnabled is already checked before this tab renders; each section self-gates on its
// own flag. Only the admin check is needed here.
function useShowSection(): boolean {
  return !!useSelector(CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector);
}
